import {
  AppBar,
  Avatar,
  Icon,
  Logo,
  SearchField,
  cn,
  type IconName,
} from "@orkestor/design-system";
import {
  OrgProvider,
  useActiveOrg,
  useAuth,
  useSysadminMe,
  useMarkNotificationsRead,
  useNotifications,
  useUnreadCount,
  type Notification,
} from "@orkestor/core";
import { routes as checkmeRoutes } from "@orkestor/skin-check-me/routes";
import { routes as pencilRoutes } from "@orkestor/skin-pencil-chief/routes";
// By subpath: the rail needs the back office's map, not its screens.
import {
  ADMIN_BASE,
  SECTIONS as ADMIN_SECTIONS,
  routes as adminRoutes,
  type AdminSection,
} from "@orkestor/skin-admin/routes";
// By subpath, not the barrel: the rail must not drag every platform screen in.

import { HOME_PATH } from "./paths";
import {
  Suspense,
  lazy,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
}

/** Whether the current path is inside the back office. Route prefix, not product
    state: Administración is an area, not a product, so nothing in the product
    switcher can answer this. */
function isAdminPath(pathname: string): boolean {
  // `ADMIN_BASE`, not the dashboard's path: they are the same string today, and
  // the day a slice moves the dashboard to `/admin/resumen` the prefix would stop
  // matching the other five sections.
  return pathname === ADMIN_BASE || pathname.startsWith(`${ADMIN_BASE}/`);
}

/** Whether to show the back office **as a place**: its rail, and the absence of
    the org selector and the product switcher.
 *
 *  The path is not enough, and shipping it as if it were is the defect adversarial
 *  review found. `SoloSysadmin` guards the route's own element — the `<Outlet>` —
 *  while the rail is a sibling *outside* it, so anyone authenticated who typed
 *  `/admin` got the whole map of the back office painted at them: the heading, and
 *  all six section names. For a `false` answer that lasted one frame before the
 *  redirect; for a *pending* one it lasted the entire round trip, and longer if the
 *  call hung. The spec says «no part of the area is rendered, not even briefly».
 *
 *  So the chrome asks the same question the gate asks, and takes only a confirmed
 *  `true` for an answer. */
function useInAdmin(): { inAdmin: boolean; undecided: boolean } {
  const { pathname } = useLocation();
  const sysadmin = useSysadminMe();
  const onPath = isAdminPath(pathname);
  return {
    inAdmin: onPath && sysadmin.data === true,
    // On an admin path without a confirmed yes: show NEITHER area. Falling back to
    // Pencil's rail would flash a product the visitor did not ask for, one frame
    // before the redirect — a smaller lie than the back office, but still a lie.
    undecided: onPath && sysadmin.data !== true,
  };
}

/** El raíl de Check Me. Corto a propósito: la consola abre por la jornada de hoy,
    y el resto de pantallas se alcanzan desde ella —el parte desde el panel, la
    persona desde el parte—. Un menú con seis entradas fingiría que son seis
    sitios independientes, y no lo son. */
const CHECKME_NAV: NavItem[] = [
  { to: checkmeRoutes.hoy, label: "Hoy", icon: "cal" },
];

/** El raíl de Orkestor: la plataforma misma. El inicio del ecosistema vive aquí
    y NO dentro de cada producto — que era lo que hacía que la portada de Orkestor
    pareciese una pantalla de Pencil. */
const ORKESTOR_NAV: NavItem[] = [
  { to: HOME_PATH, label: "Inicio", icon: "grid" },
  { to: "/organizacion", label: "Organizaciones", icon: "case" },
  { to: "/bloqueos", label: "Bloqueos", icon: "shield" },
];

/** Las cuatro áreas entre las que se navega, en un solo sitio.
 *
 *  «Entrar» en un producto es entrar del todo: su raíl es el suyo y nada más, y
 *  la manera de salir es este selector. Por eso «Inicio» ya no está en el raíl de
 *  ningún producto — el inicio es Orkestor, que es otra de las cuatro. */
interface Area {
  clave: "orkestor" | "pencil" | "checkme" | "whyhumans";
  nombre: string;
  /** El producto que hay que tener contratado, o `null` para la plataforma. */
  producto: string | null;
  destino: string | null;
  /** La clase que trae la paleta del producto, o `null` si viste la de la casa.
   *
   *  Va en la RAÍZ del shell y no dentro de las pantallas: la barra y el raíl
   *  son chrome, están fuera de la skin, y con la clase puesta más abajo Check
   *  Me se veía —así lo reportó el usuario— con la barra negra y amarilla de
   *  Orkestor encima de una consola verde. Entrar en un producto es entrar del
   *  todo. */
  paleta: string | null;
  /** Quién firma la barra. Se carga en diferido junto con el resto de la skin
   *  (`React.lazy`), porque el shell no debe arrastrar el paquete de un producto
   *  al que quizá no entres. Mientras carga no se pinta la marca anterior: se
   *  deja el hueco, que es lo que pidió el usuario —ni rastro del producto del
   *  que vienes. */
  marca: ComponentType | null;
}

const MarcaPencilChief = lazy(() =>
  import("@orkestor/skin-pencil-chief/components/Marca").then((m) => ({
    default: m.MarcaPencilChief,
  })),
);
const MarcaCheckMe = lazy(() =>
  import("@orkestor/skin-check-me/components/Marca").then((m) => ({
    default: m.MarcaCheckMe,
  })),
);

const AREAS: Area[] = [
  {
    clave: "orkestor",
    nombre: "Orkestor",
    producto: null,
    destino: HOME_PATH,
    paleta: null,
    // Sin marca propia: la plataforma firma con el logotipo de Orkestor, que es
    // el que `AppBar` pone cuando la ranura viene vacía.
    marca: null,
  },
  {
    clave: "pencil",
    nombre: "Pencil Chief",
    producto: "pencil_chief",
    destino: pencilRoutes.ofertas,
    // Viste el negro y amarillo de la casa: es el producto insignia y no tiene
    // paleta aparte.
    paleta: null,
    marca: MarcaPencilChief,
  },
  {
    clave: "checkme",
    nombre: "Check Me",
    producto: "check_me",
    destino: checkmeRoutes.hoy,
    paleta: "checkme",
    marca: MarcaCheckMe,
  },
  // Sin destino: todavía no existe. Sale en el selector porque el escaparate es
  // medio motivo de tener tres productos, y no se puede elegir porque no hay a
  // dónde ir.
  {
    clave: "whyhumans",
    nombre: "Why Humans",
    producto: "why_humans",
    destino: null,
    paleta: null,
    marca: null,
  },
];

/** El área abierta, entera. Un solo sitio del que salen la paleta, la marca y el
    raíl, para que no puedan discrepar entre sí. */
function useArea(): Area {
  const { pathname } = useLocation();
  const clave = areaDe(pathname);
  return AREAS.find((a) => a.clave === clave) ?? AREAS[0]!;
}

const NAV: NavItem[] = [
  { to: pencilRoutes.ofertas, label: "Ofertas", icon: "case" },
  { to: pencilRoutes.buscar, label: "Buscar técnicos", icon: "search" },
  { to: pencilRoutes.agenda, label: "Agenda", icon: "cal" },
  { to: pencilRoutes.mensajes, label: "Mensajes", icon: "chat" },
  { to: pencilRoutes.resenas, label: "Reseñas", icon: "star" },
];

/** Initials for an avatar: up to two words, uppercased. */
export function Shell() {
  return (
    <OrgProvider>
      <ShellInner />
    </OrgProvider>
  );
}

function ShellInner() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const area = useArea();

  useEffect(() => {
    if (status === "unauthenticated") navigate("/", { replace: true });
  }, [status, navigate]);

  if (status !== "authenticated") return null;

  return (
    // La paleta del producto, en la raíz: la barra y el raíl también son suyos.
    <div className={cn("flex h-screen flex-col bg-bg text-ink", area.paleta)}>
      <TopBar />
      <div className="grid min-h-0 flex-1 grid-cols-[222px_1fr]">
        <Sidebar />
        <main className="flex min-w-0 flex-col gap-[18px] overflow-y-auto px-[26px] py-[22px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ── Top bar ──────────────────────────────────────────────────────────────────

/** En qué área estamos, según la ruta.
 *
 *  Se deriva de la URL y no de un estado guardado a propósito: un enlace pegado
 *  en un chat tiene que abrir su producto, y un estado guardado diría que
 *  estamos en otro. */
export function areaDe(pathname: string): Area["clave"] {
  if (pathname.startsWith(`${checkmeRoutes.base}/`) || pathname === checkmeRoutes.base) {
    return "checkme";
  }
  if (pathname.startsWith(`${pencilRoutes.base}/`) || pathname === pencilRoutes.base) {
    return "pencil";
  }
  // Todo lo demás —el inicio, las organizaciones, los bloqueos— es la
  // plataforma. La trastienda no es un área de este selector: se entra por el
  // menú de la cuenta y se sale por donde se entró.
  return "orkestor";
}

/** El selector de producto, en el logo.
 *
 *  Vivía abajo a la izquierda, dentro del raíl, y ahí tenía dos problemas: el
 *  raíl es del producto en el que estás —así que el selector era parte de lo que
 *  cambiaba— y se quedaba desincronizado, diciendo «Pencil Chief» estando en
 *  Check Me. Aquí arriba está fuera de lo que cambia, y junto a la marca: entrar
 *  en un producto es entrar del todo, y esto es la puerta. */
function SelectorDeProducto() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { activeOrg } = useActiveOrg();
  // La trastienda no es un producto, así que el selector no puede nombrarla: se
  // entra por el menú de la cuenta y se sale por «Volver a Orkestor». Por la
  // ruta a secas, como el selector de organización: esconder un control no
  // cuenta nada del área a quien no debería verla.
  const enTrastienda = isAdminPath(pathname);
  const actual = areaDe(pathname);
  // `undefined` = NO CONSTA, y entonces no se afirma nada: mismo criterio que la
  // puerta de rutas y que la portada. Apagarlo todo ante la ausencia de dato
  // dejaría a cualquiera encerrado durante un despliegue.
  const contratados: string[] | undefined = activeOrg?.products ?? undefined;
  const tiene = (producto: string | null) =>
    producto === null || contratados === undefined || contratados.includes(producto);

  if (enTrastienda) return null;

  return (
    <Menu
      label="Cambiar de producto"
      trigger={
        // Sólo la flechita. El nombre del producto sobraba: lo dice el logotipo
        // que tiene al lado, y repetirlo hacía de la marca una etiqueta.
        <span className="grid h-[22px] w-[18px] place-items-center text-mute hover:text-ink">
          <Icon name="down" size={14} />
        </span>
      }
    >
      {AREAS.map((a) => {
        const disponible = tiene(a.producto) && a.destino !== null;
        return (
          <MenuItem
            key={a.clave}
            active={a.clave === actual}
            disabled={!disponible}
            {...(disponible && a.destino
              ? { onClick: () => void navigate(a.destino!) }
              : {})}
          >
            {a.nombre}
            {!tiene(a.producto) && " · no contratado"}
            {tiene(a.producto) && a.destino === null && " · en desarrollo"}
          </MenuItem>
        );
      })}
      {/* Se enseña lo que no se tiene y se dice qué hacer. Esconderlo sería más
          limpio y entonces nadie descubre que los otros productos existen. */}
      {contratados !== undefined &&
        AREAS.some((a) => a.producto && !contratados.includes(a.producto)) && (
          <div className="border-t border-line px-3 py-2 text-[11.5px] text-mute">
            Lo que no tienes contratado se pide en{" "}
            <a href="mailto:hola@orkestor.com" className="underline">
              hola@orkestor.com
            </a>
            .
          </div>
        )}
    </Menu>
  );
}

/** La marca del área abierta, en la barra.
 *
 *  Se pinta desde la ruta, así que cambia **en el mismo fotograma** en que
 *  cambia la URL: al elegir otro producto no queda el logotipo del anterior
 *  esperando a que cargue su skin. Mientras el paquete de la marca viaja se deja
 *  el hueco reservado —y no la marca de antes—, que es lo que el usuario pidió:
 *  ni rastro del producto del que vienes. */
function MarcaDelArea() {
  const area = useArea();
  if (!area.marca) return <Logo className="h-[40px]" />;
  const Marca = area.marca;
  return (
    <Suspense fallback={<span aria-hidden className="block h-[26px] w-[130px]" />}>
      <Marca />
    </Suspense>
  );
}

function TopBar() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    const term = q.trim();
    navigate(pencilRoutes.ofertasBuscando(term));
  }

  return (
    <AppBar
      brand={<MarcaDelArea />}
      product={<SelectorDeProducto />}
      org={<OrgSelector />}
      search={
        <form onSubmit={onSearch} className="hidden md:block">
          <SearchField
            value={q}
            onChange={setQ}
            placeholder="Buscar técnicos u ofertas"
            aria-label="Buscar técnicos u ofertas"
          />
        </form>
      }
      actions={<NotificationsButton />}
      user={
        <Menu
          align="right"
          trigger={
            <Avatar
              name={user?.name ?? "AC"}
              className="cursor-pointer"
              title={user?.name ?? "Cuenta"}
            />
          }
        >
          <div className="border-b border-line px-3 py-2 text-[12.5px] text-dim">
            {user?.name}
          </div>
          <AdminMenuItem />
          <MenuItem onClick={signOut}>Salir</MenuItem>
        </Menu>
      }
    />
  );
}

/** Top-bar control to **switch the active organization** — nothing else. Managing
    organizations lives in the Organizaciones product (left nav). Switching while
    inside an org's console follows into the same section of the newly-picked org;
    on any other screen it just changes the active org, keeping you where you are. */
function OrgSelector() {
  const { orgs, activeOrg, setActiveOrgId } = useActiveOrg();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Nothing in the back office is scoped to an organization — it is the one place
  // that shows *all* of them. A switcher visible above that screen is an active
  // invitation to read «todas» as «ésta». Hidden on the path alone, deliberately:
  // unlike the rail, hiding a control reveals nothing about the area.
  if (isAdminPath(pathname)) return null;
  // Pure switcher: with no organization there is nothing to switch. Creating one
  // lives in the Organizaciones product, not the top bar.
  if (!activeOrg) return null;
  const section =
    pathname.match(/^\/organizacion\/[^/]+\/([^/]+)/)?.[1] ?? null;
  return (
    <Menu
      trigger={
        <span className="flex cursor-pointer items-center gap-2 rounded-lg px-[11px] py-[6px] text-[13px] text-dim">
          <Avatar name={activeOrg.name} shape="rounded" />
          <b className="font-semibold text-ink">{activeOrg.name}</b>
          <span className="text-[11px] text-mute">▾</span>
        </span>
      }
    >
      {orgs.map((o) => (
        <MenuItem
          key={o.id}
          active={o.id === activeOrg.id}
          onClick={() => {
            setActiveOrgId(o.id);
            if (section) navigate(`/organizacion/${o.id}/${section}`);
          }}
        >
          {o.name}
        </MenuItem>
      ))}
    </Menu>
  );
}

/** The shell's notification bell: an unread badge plus a panel of the feed, both
    polled (producer-notifications Slice 1). Opening the panel marks everything
    read, so the badge reflects "unseen", and each item deep-links to its screen. */
function NotificationsButton() {
  const navigate = useNavigate();
  const feed = useNotifications();
  const unread = useUnreadCount();
  const markRead = useMarkNotificationsRead();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const count = unread.data ?? 0;
  const items = feed.data ?? [];

  const toggle = () => {
    const next = !open;
    setOpen(next);
    // Seeing the panel clears the "unseen" badge; items keep their own read mark.
    if (next && count > 0) markRead.mutate();
  };

  const openItem = (n: Notification) => {
    setOpen(false);
    if (n.kind === "message") navigate(pencilRoutes.mensajes);
    else if (n.job_id) navigate(pencilRoutes.oferta(n.job_id));
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        className="relative grid size-[34px] cursor-pointer place-items-center rounded-lg border border-line bg-surface-2 text-dim"
      >
        <Icon name="bell" size={16} />
        {count > 0 && (
          <span className="absolute -right-1 -top-1 grid min-w-[16px] place-items-center rounded-full bg-acc px-1 text-[10px] font-bold text-acc-ink">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] z-50 overflow-hidden rounded-xl border border-line bg-surface shadow-xl"
          style={{ width: "340px" }}
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <b className="text-[12.5px] font-semibold text-ink">
              Notificaciones
            </b>
            {items.length > 0 && (
              <button
                onClick={() => markRead.mutate()}
                className="text-[11.5px] text-mute hover:text-dim"
              >
                Marcar todas leídas
              </button>
            )}
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-3 py-4 text-[12.5px] text-dim">
                No tienes notificaciones.
              </div>
            ) : (
              items.map((n) => (
                <NotificationRow
                  key={n.id}
                  note={n}
                  onOpen={() => openItem(n)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationRow({
  note,
  onOpen,
}: {
  note: Notification;
  onOpen: () => void;
}) {
  const when = note.created_at ? new Date(note.created_at) : null;
  return (
    <button
      onClick={onOpen}
      className={cn(
        "flex w-full items-start gap-2.5 border-b border-line px-3 py-2.5 text-left last:border-0 hover:bg-surface-2",
        note.read_at == null && "bg-acc-sub",
      )}
    >
      <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-surface-2 text-dim">
        <Icon name={note.kind === "message" ? "chat" : "case"} size={13} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-semibold text-ink">
          {note.title}
        </span>
        <span className="block truncate text-[11.5px] text-dim">
          {note.body}
        </span>
        {when && (
          <span className="mt-0.5 block text-[10.5px] text-mute">
            {when.toLocaleDateString("es-ES", {
              day: "numeric",
              month: "short",
            })}{" "}
            ·{" "}
            {when.toLocaleTimeString("es-ES", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        )}
      </span>
    </button>
  );
}

// ── Sidebar ──────────────────────────────────────────────────────────────────

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "flex items-center gap-[10px] rounded-lg px-[10px] py-2 text-[13.5px] font-medium",
    isActive ? "bg-acc-sub font-semibold text-acc" : "text-dim hover:text-ink",
  );

/** The org console's contextual left menu (OEM-267). */
const ORG_NAV: NavItem[] = [
  { to: "perfil", label: "Perfil de empresa", icon: "case" },
  { to: "miembros", label: "Miembros", icon: "users" },
  { to: "productos", label: "Productos", icon: "grid" },
  { to: "reputacion", label: "Reputación", icon: "star" },
  { to: "facturacion", label: "Facturación", icon: "euro" },
];

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="truncate px-2 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-mute">
      {children}
    </div>
  );
}

/** El raíl ES el área en la que se ha entrado: sus pantallas, y sólo las suyas.
    Cambiar de producto no se hace desde aquí —el selector está en el logo,
    arriba, fuera de lo que cambia—, porque un selector dentro del raíl era parte
    de lo que se recambiaba al cambiar de producto y se quedaba desincronizado. */
function Sidebar() {
  const { pathname } = useLocation();
  const { inAdmin, undecided } = useInAdmin();
  const inOrgSkin =
    pathname === "/organizacion" ||
    pathname === "/bloqueos" ||
    /^\/organizacion\//.test(pathname);
  // El raíl es el del área en la que se ha entrado. Sin esto se quedaba el de
  // Pencil Chief dentro de Check Me: seis enlaces a otro producto y su nombre
  // como título, encima de las pantallas de Check Me.
  const area = areaDe(pathname);
  return (
    <nav className="flex min-h-0 flex-col gap-[3px] overflow-y-auto border-r border-line bg-surface p-[16px_12px]">
      {undecided ? null : inAdmin ? (
        <AdminScreens />
      ) : area === "checkme" ? (
        <CheckmeScreens />
      ) : area === "pencil" ? (
        <PencilScreens />
      ) : inOrgSkin ? (
        <OrgScreens />
      ) : (
        <OrkestorScreens />
      )}
      <div className="flex-1" />
    </nav>
  );
}

/** Product switcher, pinned to the bottom of the rail. Pencil Chief and
    Organizaciones are live; Check Me / Why Humans are listed but disabled until
    they ship. Picking a product navigates to that skin's home. */

/** The way into the back office, in the account menu — the only way in, since it
    is deliberately absent from the product switcher. Rendered only for a
    confirmed sysadmin: pending and failed both hide it, the same closed-by-
    default rule the route itself applies. */
function AdminMenuItem() {
  const sysadmin = useSysadminMe();
  const navigate = useNavigate();
  if (sysadmin.data !== true) return null;
  return (
    <MenuItem onClick={() => navigate(adminRoutes.resumen)}>
      Administración
    </MenuItem>
  );
}

/** The back office's rail: the whole map of the area from the first slice, with
    the sections that are not built yet shown as unavailable.

    Disabled, not dead links. A grey label that says «pendiente» is honest; a link
    that navigates to a blank screen is a bug report waiting to be filed. And
    `aria-disabled` with no `to` keeps them out of the tab order, so the rail does
    not make a keyboard user walk through five doors that do not open. */
function AdminScreens() {
  return (
    <>
      {/* The way out. Inside the area the product switcher is hidden, the org
          selector is hidden and the rail shows only admin sections, so without
          this the only exits were the browser's Back button and the top-bar
          search — which drops you into Pencil. The spec's own first requirement
          says leaving returns you to where you came from. */}
      <NavLink to={HOME_PATH} className={navLinkClass}>
        <Icon name="grid" size={16} className="opacity-85" />
        Volver a Orkestor
      </NavLink>
      <SectionLabel>Administración</SectionLabel>
      {ADMIN_SECTIONS.map((s: AdminSection) =>
        s.built ? (
          <NavLink key={s.to} to={s.to} end className={navLinkClass}>
            <Icon name={s.icon} size={16} className="opacity-85" />
            {s.label}
          </NavLink>
        ) : (
          <span
            key={s.to}
            aria-disabled="true"
            title={s.hint}
            className="flex cursor-default items-center gap-[10px] rounded-lg px-[10px] py-2 text-[13.5px] font-medium text-mute opacity-70"
          >
            <Icon name={s.icon} size={16} className="opacity-60" />
            {s.label}
            {/* In the accessible name, not only in `title` and the grey:
                `aria-disabled` on a non-focusable span tells assistive tech
                nothing, and `title` is not reliably announced — a screen-reader
                user was getting six labels, five of which silently do nothing. */}
            <span className="sr-only"> (no disponible todavía)</span>
          </span>
        ),
      )}
    </>
  );
}

/** El área de plataforma: el inicio del ecosistema y lo que es de Orkestor y no
    de ningún producto. Antes esto no existía como área — el inicio colgaba del
    raíl de Pencil y las organizaciones vivían en un selector aparte. */
function OrkestorScreens() {
  return (
    <>
      <SectionLabel>Orkestor</SectionLabel>
      {ORKESTOR_NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end className={navLinkClass}>
          <Icon name={n.icon} size={16} className="opacity-85" />
          {n.label}
        </NavLink>
      ))}
    </>
  );
}

function CheckmeScreens() {
  return (
    <>
      <SectionLabel>Check Me</SectionLabel>
      {CHECKME_NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end className={navLinkClass}>
          <Icon name={n.icon} size={16} className="opacity-85" />
          {n.label}
        </NavLink>
      ))}
    </>
  );
}

function PencilScreens() {
  return (
    <>
      <SectionLabel>Pencil Chief</SectionLabel>
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end className={navLinkClass}>
          <Icon name={n.icon} size={16} className="opacity-85" />
          {n.label}
        </NavLink>
      ))}
    </>
  );
}

/** The Organizaciones product's screens: the org list, then — when an organization
    is in context — its console sections plus Bloqueos. */
function OrgScreens() {
  const { pathname } = useLocation();
  const { orgs, activeOrg } = useActiveOrg();
  const orgId =
    pathname.match(/^\/organizacion\/([^/]+)(?:\/|$)/)?.[1] ??
    activeOrg?.id ??
    null;
  const name = orgs.find((o) => o.id === orgId)?.name ?? "Organización";
  return (
    <>
      <NavLink to="/organizacion" end className={navLinkClass}>
        <Icon name="grid" size={16} className="opacity-85" />
        Mis organizaciones
      </NavLink>
      {orgId && (
        <>
          <SectionLabel>{name}</SectionLabel>
          {ORG_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={`/organizacion/${orgId}/${n.to}`}
              className={navLinkClass}
            >
              <Icon name={n.icon} size={16} className="opacity-85" />
              {n.label}
            </NavLink>
          ))}
          <NavLink to="/bloqueos" end className={navLinkClass}>
            <Icon name="alert" size={16} className="opacity-85" />
            Bloqueos
          </NavLink>
        </>
      )}
    </>
  );
}

// ── Dropdown menu (lightweight, click-outside) ───────────────────────────────

function Menu({
  trigger,
  children,
  align = "left",
  label,
}: {
  trigger: ReactNode;
  children: ReactNode;
  /** Nombre accesible del botón que abre el menú. Sin él, un menú cuyo
      disparador cambia de texto —el del producto dice «Pencil Chief» o «Check
      Me» según dónde estés— no se puede nombrar ni por un lector de pantalla ni
      por un test. */
  label?: string;
  /** Which edge of the trigger the panel hangs from. Anything living in the
      right half of the top bar needs `"right"`: the default drops the panel to
      the trigger's right and it runs off the viewport, with no scrollbar and no
      error to notice — which is exactly how the account menu shipped clipped. */
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center"
        {...(label ? { "aria-label": label } : {})}
      >
        {trigger}
      </button>
      {open && (
        <div
          className={cn(
            "absolute top-[calc(100%+6px)] z-50 min-w-[200px] overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-xl",
            align === "right" ? "right-0" : "left-0",
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  active,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "block w-full px-3 py-2 text-left text-[13px]",
        disabled
          ? "cursor-default text-mute"
          : "text-dim hover:bg-surface-2 hover:text-ink",
        active && "font-semibold text-acc",
      )}
    >
      {children}
    </button>
  );
}
